<template>
  <div>
    <HeroHeader
      eyebrow="PLANILLA"
      title="Nómina"
      :counter-num="activeTab === 'personal' ? activeStaff : visiblePayslips.length"
      :counter-unit="activeTab === 'personal' ? 'colaboradores' : 'boletas'"
    >
      <template #toolbar>
        <button v-if="activeTab === 'payroll'" class="btn btn--small" @click="exportToExcel">Exportar Excel</button>
        <button v-if="activeTab === 'personal'" class="btn" @click="showNewStaff = true">+ Agregar</button>
      </template>
    </HeroHeader>

    <TabBar
      :tabs="[{label:'Personal',value:'personal'},{label:'Planilla del mes',value:'payroll'}]"
      v-model="activeTab"
    />

    <!-- ============================================================ -->
    <!-- TAB: PERSONAL -->
    <!-- ============================================================ -->
    <div v-if="activeTab === 'personal'">
      <div class="filter-bar filter-bar--no-top">
        <SearchInput v-model="staffSearch" placeholder="Buscar por nombre o cargo…"/>
        <select v-model="filterEstate">
          <option value="All">Todos los locales</option>
          <option v-for="e in estateOptions" :key="e.id" :value="e.id">{{ e.description }}</option>
        </select>
        <SortSelect :options="staffSortOptions" v-model="staffSortBy" />
      </div>

      <div class="dt-wrapper">
        <table class="dt" v-if="filteredStaff.length">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Cargo</th>
              <th>Local</th>
              <th>Tipo</th>
              <th>Sueldo / Tarifa</th>
              <th>Estado</th>
              <th aria-label="Acciones"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in sortedStaff" :key="s.id">
              <td><span class="dt-cell__primary">{{ s.name }} {{ s.lastName }}</span></td>
              <td>{{ s.position }}</td>
              <td>{{ s.estate ? s.estate.description : '—' }}</td>
              <td>
                <span class="type-chip" :class="s.type === 'fixed' ? 'type-chip--fixed' : 'type-chip--variable'">
                  {{ s.type === 'fixed' ? 'Fijo' : (s.rateUnit === 'day' ? 'Variable/día' : 'Variable/hora') }}
                </span>
              </td>
              <td>
                <span v-if="s.type === 'fixed'">{{ formatAmount(s.monthlySalary) }}/mes</span>
                <span v-else>{{ formatAmount(s.rate) }}/{{ s.rateUnit === 'day' ? 'día' : 'hora' }}</span>
              </td>
              <td>
                <span class="badge" :class="s.active ? 'badge--success' : 'badge--muted'">{{ s.active ? 'Activo' : 'Inactivo' }}</span>
              </td>
              <td>
                <div class="dt-actions">
                  <span class="dt-icon" title="Editar" @click="openEditStaff(s)">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <EmptyState v-else title="Sin personal" message="No hay colaboradores. Agrega uno con el botón +."/>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- TAB: PLANILLA DEL MES -->
    <!-- ============================================================ -->
    <div v-if="activeTab === 'payroll'">
      <!-- Selector de mes + quincena — una sola banda alineada -->
      <div class="filter-row">
        <MonthNav v-model="period"/>

        <!-- Half-of-month selector (does not refetch — filters loaded payslips) -->
        <ChipsBar
          :chips="[{label:'1ª quincena',value:1},{label:'2ª quincena',value:2}]"
          v-model="selectedHalf"
        />
      </div>

      <!-- KPI grid — computed from the visible half; monthly total shown as sub -->
      <div class="kpi-grid">
        <div class="kpi-card">
          <span class="kpi-card__label">Bruto total</span>
          <span class="kpi-card__value">{{ formatAmount(halfTotals.gross) }}</span>
          <span class="kpi-card__sub">{{ halfLabel }} · {{ formatPeriodLabel(period) }}</span>
        </div>
        <div class="kpi-card kpi-card--warning">
          <span class="kpi-card__label">Descuentos</span>
          <span class="kpi-card__value">{{ formatAmount(halfTotals.deductions) }}</span>
        </div>
        <div class="kpi-card kpi-card--success">
          <span class="kpi-card__label">Neto total</span>
          <span class="kpi-card__value">{{ formatAmount(halfTotals.net) }}</span>
          <span class="kpi-card__sub">Total del mes: {{ formatAmount(header.totals && header.totals.net) }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-card__label">Boletas</span>
          <span class="kpi-card__value">{{ halfTotals.finalizedCount }} / {{ halfTotals.activeCount }}</span>
          <div class="kpi-card__bar">
            <div class="kpi-card__bar-fill" :style="{ width: finalizedPct + '%' }"></div>
          </div>
          <span class="kpi-card__sub">{{ finalizedPct }}% finalizadas</span>
        </div>
      </div>

      <!-- Generate / refresh — state computed per visible half -->
      <div class="payroll-generate">
        <span class="payroll-generate__info">
          <span v-if="!halfHasPayslips">Sin {{ halfLabel }} generada para este período.</span>
          <span v-else-if="halfIsClosed" class="badge badge--success">{{ halfLabel }} cerrada</span>
          <span v-else>{{ halfTotals.draftCount }} borradores · {{ halfTotals.finalizedCount }} finalizadas</span>
          <span v-if="filterEstate !== 'All'">Genera boletas de todos los locales</span>
        </span>
        <div class="payroll-generate__actions">
          <button v-if="halfTotals.draftCount > 0 && !closeMode" @click="openClose" class="btn btn--outline">
            Cerrar quincena
          </button>
          <button v-if="!halfIsClosed" :disabled="generatingDraft" :class="{'is-loading': generatingDraft}" @click="generateDraft" class="btn">
            <span v-if="!generatingDraft">{{ halfHasPayslips ? `Actualizar ${halfLabel}` : `Generar ${halfLabel}` }}</span>
          </button>
          <button v-else :disabled="generatingDraft" :class="{'is-loading': generatingDraft}" @click="generateDraft" class="btn btn--outline">
            <span v-if="!generatingDraft">Reemitir borrador</span>
          </button>
        </div>
      </div>

      <!-- Close-half confirmation (inline — finalizes every draft of the visible half at once) -->
      <div v-if="closeMode" class="payroll-close">
        <div class="ps-action-form">
          <div class="ps-action-form__title">Cerrar {{ halfLabel }} · {{ formatPeriodLabel(period) }}</div>
          <p class="payroll-close__scope">
            Se finalizarán <strong>{{ halfTotals.draftCount }}</strong>
            {{ halfTotals.draftCount === 1 ? 'boleta' : 'boletas' }} en borrador de
            <strong>{{ closeEstateLabel }}</strong>.
          </p>
          <div class="form-group ps-field">
            <label for="close-date">Fecha de pago</label>
            <input v-model="closeDate" type="date" id="close-date" class="input">
            <span v-if="!closeDate" class="error">Ingresa la fecha de pago</span>
          </div>
          <div class="form-group ps-field">
            <label for="close-method">Método</label>
            <select v-model="closeMethod" class="select" id="close-method">
              <option value="cash">Efectivo</option>
              <option value="transfer">Transferencia</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div class="payroll-close__actions">
            <button :disabled="closing || !closeDate" :class="{'is-loading': closing}" @click="confirmClose" class="btn">
              <span v-if="!closing">Confirmar cierre</span>
            </button>
            <button @click="closeMode = false" class="btn btn--outline">Cancelar</button>
          </div>
        </div>
      </div>

      <!-- Row controls: search + local + sort (filter the table, never the KPIs) -->
      <div class="filter-bar filter-bar--no-top">
        <SearchInput v-model="payslipSearch" placeholder="Buscar por nombre o cargo…"/>
        <select v-model="filterEstate">
          <option value="All">Todos los locales</option>
          <option v-for="e in estateOptions" :key="e.id" :value="e.id">{{ e.description }}</option>
        </select>
        <SortSelect :options="payslipSortOptions" v-model="payslipSortBy" />
      </div>

      <!-- Status chips — count over the estate/half set; filter rows only -->
      <div class="payroll-half-selector">
        <ChipsBar :chips="statusChips" v-model="payslipStatusFilter" />
      </div>

      <!-- Payslips table — visible half only -->
      <div class="dt-wrapper">
        <table class="dt" v-if="sortedPayslips.length">
          <thead>
            <tr>
              <th>Colaborador</th>
              <th>Cargo</th>
              <th>Local</th>
              <th>Base</th>
              <th>Neto</th>
              <th>Estado</th>
              <th aria-label="Acciones"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in sortedPayslips" :key="p.id" :class="{'payslip-row--voided': p.status === 'voided'}">
              <td><span class="dt-cell__primary">{{ p.workerName }} {{ p.workerLastName }}</span></td>
              <td>{{ p.position }}</td>
              <td>{{ p.estate ? p.estate.description : '—' }}</td>
              <td>
                <span class="dt-cell">
                  <span class="dt-cell__primary dt-cell--numeric">{{ formatAmount(p.base ? p.base.amount : 0) }}</span>
                  <span class="dt-cell__secondary">{{ baseContext(p.base) }}</span>
                </span>
              </td>
              <td class="dt-cell--numeric">{{ formatAmount(p.netTotal) }}</td>
              <td>
                <span class="payslip-status" :class="'payslip-status--' + p.status">
                  <svg v-if="p.status === 'draft'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  <svg v-else-if="p.status === 'finalized'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <svg v-else-if="p.status === 'voided'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                  {{ statusLabel(p.status) }}
                </span>
              </td>
              <td>
                <div class="dt-actions">
                  <span class="dt-icon" title="Ver / editar" @click="openPayslip(p)">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  </span>
                  <!-- Reissue targets the visible half (generateDraft uses selectedHalf) -->
                  <span v-if="p.status === 'voided'" class="dt-icon" title="Reemitir borrador" @click="generateDraft">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
                    </svg>
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <EmptyState v-else-if="estateHalfPayslips.length" title="Sin resultados" message="Ningún colaborador coincide con la búsqueda o el filtro de estado."/>
        <EmptyState v-else-if="!generatingDraft && header.period !== undefined" title="Sin boletas" :message="`Genera la ${halfLabel} para crear las boletas de este período.`"/>
      </div>
    </div>

    <!-- Sidebars -->
    <NewStaff
      :show-navbar="showNewStaff"
      title="Agregar personal"
      :class="{'show': showNewStaff}"
      @close="showNewStaff = false"
      @created="onStaffCreated"
    />
    <UpdateStaff
      :show-navbar="showUpdateStaff"
      :data="selectedStaff"
      title="Editar personal"
      :class="{'show': showUpdateStaff}"
      @close="showUpdateStaff = false"
      @updated="onStaffUpdated"
    />
    <PayslipSidebar
      :show-navbar="showPayslip"
      :payslip="selectedPayslip"
      :class="{'show': showPayslip}"
      @close="showPayslip = false"
      @updated="onPayslipUpdated"
      @finalized="onPayslipFinalized"
      @voided="onPayslipVoided"
    />
  </div>
</template>

<script>
import { db, functions } from '@/firebase/firebaseConfig'
import HeroHeader from '@/components/HeroHeader'
import TabBar from '@/components/TabBar'
import ChipsBar from '@/components/ChipsBar'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import MonthNav from '@/components/MonthNav'
import SearchInput from '@/components/SearchInput'
import { sortItems } from '@/utils/sort'
import { currentPeriod, formatPeriod, limaNow } from '@/utils/period'
import NewStaff from './NewStaff'
import UpdateStaff from './UpdateStaff'
import PayslipSidebar from './PayslipSidebar'

export default {
  name: 'Payroll',
  components: { HeroHeader, TabBar, ChipsBar, EmptyState, SortSelect, MonthNav, NewStaff, UpdateStaff, PayslipSidebar, SearchInput },
  data () {
    return {
      activeTab: 'personal',
      // Shared local (estate) filter — persists across tabs. Options are derived
      // from the staff (only locales that actually have personnel), not the full
      // estates collection.
      filterEstate: 'All',
      // Staff tab
      staff: [],
      staffSearch: '',
      staffSortBy: 'name',
      staffSortOptions: [
        { value: 'name', label: 'Nombre (A–Z)', field: 'lastName', type: 'string', dir: 'asc' },
        { value: 'name_desc', label: 'Nombre (Z–A)', field: 'lastName', type: 'string', dir: 'desc' },
        { value: 'position', label: 'Cargo (A–Z)', field: 'position', type: 'string', dir: 'asc' },
        { value: 'salary_desc', label: 'Sueldo (mayor a menor)', field: 'monthlySalary', type: 'number', dir: 'desc' }
      ],
      showNewStaff: false,
      showUpdateStaff: false,
      selectedStaff: {},
      // Payroll tab
      period: currentPeriod(),
      selectedHalf: this.currentHalf(),
      payslips: [],
      header: {},
      generatingDraft: false,
      // Bulk close (finalize all drafts of the visible half at once)
      closeMode: false,
      closeDate: '',
      closeMethod: 'cash',
      closing: false,
      showPayslip: false,
      selectedPayslip: {},
      // Payslips list controls (rows only — never feed the KPIs)
      payslipSearch: '',
      payslipSortBy: 'status',
      payslipSortOptions: [
        { value: 'status', label: 'Estado (borrador primero)' },
        { value: 'worker', label: 'Colaborador (A–Z)', field: 'workerLastName', type: 'string', dir: 'asc' },
        { value: 'net_desc', label: 'Neto (mayor a menor)', field: 'netTotal', type: 'number', dir: 'desc' }
      ],
      payslipStatusFilter: 'all'
    }
  },
  computed: {
    // Locales for the filter dropdown = only those that have personnel (derived
    // from staff), so galerías/estates without payroll don't appear. Distinct + sorted.
    estateOptions () {
      const seen = new Map()
      this.staff.forEach(s => {
        if (s.estate && s.estate.id && !seen.has(s.estate.id)) {
          seen.set(s.estate.id, { id: s.estate.id, description: s.estate.description })
        }
      })
      return [...seen.values()].sort((a, b) => (a.description || '').localeCompare(b.description || ''))
    },
    filteredStaff () {
      const q = this.staffSearch.trim().toLowerCase()
      return this.staff.filter(s => {
        // Local filter first (shared across tabs); staff with no estate only show under "All".
        if (this.filterEstate !== 'All' && !(s.estate && s.estate.id === this.filterEstate)) return false
        if (!q) return true
        const full = `${s.name} ${s.lastName} ${s.position}`.toLowerCase()
        return full.includes(q)
      })
    },
    sortedStaff () {
      return sortItems(this.filteredStaff, this.staffSortOptions.find(o => o.value === this.staffSortBy))
    },
    activeStaff () { return this.staff.filter(s => s.active !== false).length },
    // Base link of the payslip chain: visible half + local filter only.
    // This is what the KPIs are computed from — search/status never touch it.
    estateHalfPayslips () {
      return this.payslips.filter(p =>
        (p.half || 1) === this.selectedHalf &&
        (this.filterEstate === 'All' || (p.estate && p.estate.id === this.filterEstate))
      )
    },
    // Alias — also drives the HeroHeader counter so it respects the local filter.
    visiblePayslips () {
      return this.estateHalfPayslips
    },
    halfLabel () { return this.selectedHalf === 2 ? '2ª quincena' : '1ª quincena' },
    // Whether the visible half has any payslips at all.
    halfHasPayslips () { return this.estateHalfPayslips.length > 0 },
    // Display-only: % of active (non-voided) payslips that are finalized. Feeds the KPI progress bar.
    finalizedPct () {
      const total = this.halfTotals.activeCount
      return total > 0 ? Math.round((this.halfTotals.finalizedCount / total) * 100) : 0
    },
    // KPIs recomputed from local + visible half only (header.totals is monthly = both halves).
    halfTotals () {
      const active = this.estateHalfPayslips.filter(p => p.status !== 'voided')
      return active.reduce((acc, p) => {
        acc.gross += p.grossTotal || 0
        acc.deductions += p.deductionsTotal || 0
        acc.net += p.netTotal || 0
        acc.activeCount += 1
        if (p.status === 'draft') acc.draftCount += 1
        if (p.status === 'finalized') acc.finalizedCount += 1
        return acc
      }, { gross: 0, deductions: 0, net: 0, activeCount: 0, draftCount: 0, finalizedCount: 0 })
    },
    // The half is "closed" when it has non-voided payslips and none are still drafts.
    halfIsClosed () {
      return this.halfTotals.activeCount > 0 && this.halfTotals.draftCount === 0
    },
    // Row-level filtering: status chips + text search over the estate/half set.
    // Never used by the KPIs.
    tablePayslips () {
      const q = this.payslipSearch.trim().toLowerCase()
      return this.estateHalfPayslips.filter(p => {
        if (this.payslipStatusFilter !== 'all' && p.status !== this.payslipStatusFilter) return false
        if (!q) return true
        const full = `${p.workerName} ${p.workerLastName} ${p.position}`.toLowerCase()
        return full.includes(q)
      })
    },
    sortedPayslips () {
      if (this.payslipSortBy === 'status') {
        const ORDER = { draft: 0, finalized: 1, voided: 2 }
        return [...this.tablePayslips].sort((a, b) =>
          (ORDER[a.status] - ORDER[b.status]) ||
          (a.workerLastName || '').localeCompare(b.workerLastName || '')
        )
      }
      return sortItems(this.tablePayslips, this.payslipSortOptions.find(o => o.value === this.payslipSortBy))
    },
    // Status chips with counts over the estate/half set (independent of the active chip/search).
    statusChips () {
      const base = this.estateHalfPayslips
      const countBy = s => base.filter(p => p.status === s).length
      return [
        { label: 'Todos', value: 'all', count: base.length },
        { label: 'Borrador', value: 'draft', count: countBy('draft') },
        { label: 'Finalizada', value: 'finalized', count: countBy('finalized') },
        { label: 'Anulada', value: 'voided', count: countBy('voided') }
      ]
    },
    // Human label for the local scope of the bulk close — respects the shared estate filter.
    closeEstateLabel () {
      if (this.filterEstate === 'All') return 'todos los locales'
      const e = this.estateOptions.find(o => o.id === this.filterEstate)
      return e ? e.description : 'el local seleccionado'
    }
  },
  watch: {
    // Cambiar de mes recarga la planilla (antes lo hacía shiftPeriod a mano)
    period () { this.loadPayroll() },
    activeTab (val) {
      if (val === 'payroll' && !this.payslips.length && !this.header.period) this.loadPayroll()
      if (val === 'personal' && !this.staff.length) this.loadStaff()
    }
  },
  async created () { await this.loadStaff() },
  methods: {
    // Default half from today's Lima date: day <= 15 → 1st half, else 2nd.
    currentHalf () {
      return limaNow().getDate() <= 15 ? 1 : 2
    },
    formatPeriodLabel (period) { return formatPeriod(period) },
    formatAmount (n) {
      return 'S/ ' + Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0)
    },
    statusLabel (s) {
      return { draft: 'Borrador', finalized: 'Finalizada', voided: 'Anulada' }[s] || s
    },
    // Display-only: how the base was derived. Fixed → "½ sueldo"; variable → "N u × S/ tarifa".
    baseContext (base) {
      if (!base) return ''
      if (base.kind === 'fixed') return '½ sueldo'
      const unit = base.rateUnit === 'day' ? 'd' : 'h'
      return `${base.units || 0} ${unit} × ${this.formatAmount(base.rate)}`
    },
    round2 (n) { return Math.round((n || 0) * 100) / 100 },
    // Timestamp → dd/mm/yyyy (empty when unpaid). Pattern from PayslipSidebar.
    formatExportDate (ts) {
      if (!ts) return ''
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return String(d.getDate()).padStart(2, '0') + '/' +
        String(d.getMonth() + 1).padStart(2, '0') + '/' + d.getFullYear()
    },
    // Exports the estate + half set (audit-grade: includes voided, ignores search/status).
    exportToExcel () {
      const rows = this.estateHalfPayslips
      if (!rows.length) { this.$toast.error('No hay boletas para exportar'); return }
      const halfLabel = this.halfLabel
      const estateLabel = this.filterEstate === 'All'
        ? 'Todos los locales'
        : ((this.estateOptions.find(e => e.id === this.filterEstate) || {}).description || 'Local')
      import('@/vendor/Export2Excel').then(excel => {
        const data = rows.map(p => {
          const base = p.base ? (p.base.amount || 0) : 0
          return [
            `${p.workerName || ''} ${p.workerLastName || ''}`.trim(),
            p.estate ? p.estate.description : '—',
            p.position || '',
            p.half === 2 ? '2ª' : '1ª',
            base,
            this.round2((p.grossTotal || 0) - base),
            p.grossTotal || 0,
            p.deductionsTotal || 0,
            p.netTotal || 0,
            this.statusLabel(p.status),
            this.formatExportDate(p.paymentDate)
          ]
        })
        excel.export_json_to_excel({
          header: ['Colaborador', 'Local', 'Cargo', 'Quincena', 'Base', 'Ajustes', 'Bruto', 'Descuentos', 'Neto', 'Estado', 'Fecha de pago'],
          data,
          filename: `Planilla ${this.formatPeriodLabel(this.period)} - ${halfLabel} - ${estateLabel}`,
          autoWidth: true,
          bookType: 'xlsx'
        })
      }).catch(() => this.$toast.error('Error al exportar'))
    },
    async loadStaff () {
      try {
        this.$store.dispatch('showProgress')
        const snap = await db.collection('staff').orderBy('lastName').get()
        this.staff = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) {
        this.$toast.error('Error al cargar personal')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    async loadPayroll () {
      try {
        this.$store.dispatch('showProgress')
        const [psSnap, headerSnap] = await Promise.all([
          db.collection('payslips').where('period', '==', this.period).orderBy('workerLastName').get(),
          db.doc(`payrollPeriods/${this.period}`).get()
        ])
        this.payslips = psSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.header = headerSnap.exists ? { id: headerSnap.id, ...headerSnap.data() } : {}
      } catch (e) {
        this.$toast.error('Error al cargar planilla')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    async generateDraft () {
      try {
        this.generatingDraft = true
        const fn = functions.httpsCallable('planilla-generateDraft')
        const res = await fn({ period: this.period, half: this.selectedHalf })
        if (res.data.ok) {
          const { created, updated, skipped } = res.data
          this.$toast.success(`Planilla generada: ${created} nuevas, ${updated} actualizadas, ${skipped} omitidas`)
          await this.loadPayroll()
        }
      } catch (e) {
        this.$toast.error(e.message || 'Error al generar planilla')
      } finally {
        this.generatingDraft = false
      }
    },
    // Default payment date for the bulk close — same criterion as the sidebar prefill:
    // 1st half → YYYY-MM-15, 2nd half → last day of the period's month.
    defaultCloseDate () {
      if (this.selectedHalf === 1) return `${this.period}-15`
      const [y, m] = this.period.split('-').map(Number)
      const lastDay = new Date(y, m, 0).getDate() // day 0 of next month = last day of this month
      return `${this.period}-${String(lastDay).padStart(2, '0')}`
    },
    openClose () {
      this.closeDate = this.defaultCloseDate()
      this.closeMethod = 'cash'
      this.closeMode = true
    },
    async confirmClose () {
      if (!this.closeDate) { this.$toast.error('Ingresa la fecha de pago'); return }
      try {
        this.closing = true
        const fn = functions.httpsCallable('planilla-closePayroll')
        const res = await fn({
          period: this.period,
          half: this.selectedHalf,
          estateId: this.filterEstate === 'All' ? null : this.filterEstate,
          paymentDate: this.closeDate,
          paymentMethod: this.closeMethod
        })
        if (res.data.ok) {
          this.$toast.success(`Planilla cerrada: ${res.data.finalized} boletas finalizadas`)
          await this.loadPayroll()
          this.closeMode = false
        }
      } catch (e) {
        this.$toast.error(e.message || 'Error al cerrar la planilla')
      } finally {
        this.closing = false
      }
    },
    // Staff events
    openEditStaff (s) { this.selectedStaff = s; this.showUpdateStaff = true },
    onStaffCreated (obj) { this.staff.unshift(obj) },
    onStaffUpdated (obj) {
      const idx = this.staff.findIndex(s => s.id === obj.id)
      if (idx !== -1) Object.assign(this.staff[idx], obj)
    },
    // Payslip events
    openPayslip (p) { this.selectedPayslip = p; this.showPayslip = true },
    onPayslipUpdated (obj) {
      const idx = this.payslips.findIndex(p => p.id === obj.id)
      if (idx !== -1) Object.assign(this.payslips[idx], obj)
    },
    async onPayslipFinalized (id) {
      await this.loadPayroll()
    },
    async onPayslipVoided (id) {
      await this.loadPayroll()
    }
  }
}
</script>
